// Grain tile (§11.19): 256×256 zero-mean, unit-variance noise, high-passed so it reads as fine grain
// (blue-ish spectrum), generated deterministically from a seed (the photo), so the preview and every
// re-export of the same photo show the same grain.
export const GRAIN_TILE = 256;

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function grainSeed(name: string, size: number, lastModified: number): number {
  let h = 0x811c9dc5;
  const s = `${name}|${size}|${lastModified}`;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function makeGrainTile(seed: number): Float32Array {
  const N = GRAIN_TILE;
  const r = mulberry32(seed);
  const white = new Float32Array(N * N);
  // Approximately Gaussian white noise (sum of 4 uniforms).
  for (let i = 0; i < white.length; i++) white[i] = r() + r() + r() + r() - 2;
  // High-pass: subtract a wrap-around 5×5 binomial blur (removes the low-frequency mottling).
  const k = [1, 4, 6, 4, 1];
  const tmp = new Float32Array(N * N),
    low = new Float32Array(N * N);
  for (let y = 0; y < N; y++)
    for (let x = 0; x < N; x++) {
      let a = 0;
      for (let i = -2; i <= 2; i++) a += k[i + 2]! * white[y * N + ((x + i + N) % N)]!;
      tmp[y * N + x] = a / 16;
    }
  for (let y = 0; y < N; y++)
    for (let x = 0; x < N; x++) {
      let a = 0;
      for (let i = -2; i <= 2; i++) a += k[i + 2]! * tmp[((y + i + N) % N) * N + x]!;
      low[y * N + x] = a / 16;
    }
  const out = new Float32Array(N * N);
  let mean = 0;
  for (let i = 0; i < out.length; i++) {
    out[i] = white[i]! - low[i]!;
    mean += out[i]!;
  }
  mean /= out.length;
  let v = 0;
  for (let i = 0; i < out.length; i++) v += (out[i]! - mean) ** 2;
  const sd = Math.sqrt(v / out.length) || 1;
  for (let i = 0; i < out.length; i++) out[i] = (out[i]! - mean) / sd;
  return out;
}

/** Grain cell size in output pixels: grows with the grain size slider and with the output's long edge. */
export const grainCell = (size: number, outW: number, outH: number) =>
  (1 + (3 * size) / 100) * Math.max(1, Math.max(outW, outH) / 2000);
